import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { articles } from '@/mocks/articles';
import { categories } from '@/mocks/categories';

const categoryNames: Record<string, string> = {
  'xinggan-chuanda': '性感穿搭技巧',
  'qizhi-tisheng': '气质提升方法',
  'xinggan-zhuangrong': '性感妆容教程',
  'zixin-fengge': '自信女性风格',
  'shishang-zaoxing': '时尚性感造型',
};

function renderMarkdownContent(content: string) {
  const lines = content.split('\n');
  const elements: JSX.Element[] = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];

    if (line.startsWith('## ')) {
      elements.push(
        <h2 key={i} className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mt-8 mb-4">
          {line.replace('## ', '')}
        </h2>
      );
      i++;
    } else if (line.startsWith('### ')) {
      elements.push(
        <h3 key={i} className="text-lg md:text-xl font-heading font-semibold text-foreground-800 mt-6 mb-3">
          {line.replace('### ', '')}
        </h3>
      );
      i++;
    } else if (line.startsWith('- ')) {
      const listItems: string[] = [];
      while (i < lines.length && lines[i].startsWith('- ')) {
        listItems.push(lines[i].replace('- ', ''));
        i++;
      }
      elements.push(
        <ul key={i} className="list-disc pl-5 space-y-1.5 mb-4">
          {listItems.map((item, idx) => (
            <li key={idx} className="text-foreground-700 leading-relaxed">
              {item}
            </li>
          ))}
        </ul>
      );
    } else if (line.trim() === '') {
      i++;
    } else {
      let paragraph = line;
      i++;
      while (i < lines.length && lines[i].trim() !== '' && !lines[i].startsWith('## ') && !lines[i].startsWith('### ') && !lines[i].startsWith('- ')) {
        paragraph += '\n' + lines[i];
        i++;
      }
      elements.push(
        <p key={i} className="text-foreground-700 leading-relaxed mb-4">
          {paragraph}
        </p>
      );
    }
  }

  return elements;
}

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = articles.find((a) => a.slug === slug);

  if (!article) {
    return (
      <>
        <ScrollToTop />
        <Navbar />
        <main className="pt-16 md:pt-18 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="w-16 h-16 flex items-center justify-center rounded-full bg-background-100 text-foreground-400 mx-auto mb-4">
              <i className="ri-file-unknow-line text-2xl" />
            </div>
            <h1 className="text-xl font-heading font-semibold text-foreground-900 mb-2">文章未找到</h1>
            <p className="text-sm text-foreground-600 mb-6">该文章可能已被删除或链接无效</p>
            <Link
              to="/"
              className="px-5 py-2.5 bg-primary-500 text-background-50 rounded-md font-medium text-sm hover:bg-primary-600 transition-colors whitespace-nowrap"
            >
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const catName = categoryNames[article.category] || article.category;
  const relatedArticles = articles
    .filter((a) => a.category === article.category && a.slug !== article.slug)
    .slice(0, 3);

  return (
    <>
      <ScrollToTop />
      <Navbar />

      <main className="pt-16 md:pt-18">
        <Breadcrumb
          items={[
            { label: catName, href: `/category/${article.category}` },
            { label: article.title },
          ]}
        />

        {/* Article Header */}
        <article className="w-full max-w-3xl mx-auto px-4 md:px-6">
          <header className="mb-8">
            <Link
              to={`/category/${article.category}`}
              className="inline-block px-3 py-1 text-xs font-medium bg-primary-100 text-primary-700 rounded-full mb-4 hover:bg-primary-200 transition-colors whitespace-nowrap"
            >
              {catName}
            </Link>
            <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-foreground-900 mb-4 leading-tight text-balance">
              {article.title}
            </h1>
            <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-500">
              <span className="flex items-center gap-1.5">
                <i className="ri-calendar-line" />
                {article.date}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-time-line" />
                {article.readTime} 分钟阅读
              </span>
            </div>
            <div className="flex flex-wrap gap-1.5 mt-4">
              {article.tags.map((tag) => (
                <span
                  key={tag}
                  className="px-2.5 py-1 text-xs bg-secondary-100 text-secondary-700 rounded-full whitespace-nowrap"
                >
                  {tag}
                </span>
              ))}
            </div>
          </header>

          {/* Cover Image */}
          <div className="w-full rounded-lg overflow-hidden mb-10">
            <img
              src={article.coverImage}
              alt={article.title}
              title={`${article.title} - 美女性感魅力养成指南`}
              className="w-full h-auto object-cover"
            />
          </div>

          {/* Article Content */}
          <div className="prose-custom text-base leading-relaxed">
            {renderMarkdownContent(article.content)}
          </div>

          {/* Share & Navigation */}
          <div className="mt-12 pt-8 border-t border-background-200/70">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <span className="text-sm text-foreground-600">分享文章：</span>
                <a href="#" className="w-9 h-9 flex items-center justify-center rounded-full bg-background-100 text-foreground-600 hover:bg-primary-100 hover:text-primary-600 transition-colors" aria-label="分享到微信">
                  <i className="ri-wechat-line" />
                </a>
                <a href="#" className="w-9 h-9 flex items-center justify-center rounded-full bg-background-100 text-foreground-600 hover:bg-primary-100 hover:text-primary-600 transition-colors" aria-label="分享到微博">
                  <i className="ri-weibo-line" />
                </a>
                <a href="#" className="w-9 h-9 flex items-center justify-center rounded-full bg-background-100 text-foreground-600 hover:bg-primary-100 hover:text-primary-600 transition-colors" aria-label="复制链接">
                  <i className="ri-link" />
                </a>
              </div>
              <Link
                to={`/category/${article.category}`}
                className="flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700 font-medium whitespace-nowrap transition-colors"
              >
                查看更多{catName} <i className="ri-arrow-right-line" />
              </Link>
            </div>
          </div>

          {/* Related Articles */}
          {relatedArticles.length > 0 && (
            <section className="mt-10 mb-10">
              <h2 className="text-xl font-heading font-bold text-foreground-900 mb-5">相关文章</h2>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {relatedArticles.map((ra) => (
                  <Link
                    key={ra.slug}
                    to={`/article/${ra.slug}`}
                    className="group block p-4 bg-background-50 border border-background-200/70 rounded-lg hover:border-primary-200 transition-all"
                  >
                    <div className="w-full h-32 rounded-md overflow-hidden mb-3">
                      <img
                        src={ra.coverImage}
                        alt={ra.title}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <h3 className="text-sm font-medium text-foreground-800 group-hover:text-primary-600 transition-colors line-clamp-2">
                      {ra.title}
                    </h3>
                    <span className="text-xs text-foreground-500 mt-1.5 block">{ra.date}</span>
                  </Link>
                ))}
              </div>
            </section>
          )}
        </article>
      </main>

      <Footer />

      <script type="application/ld+json" dangerouslySetInnerHTML={{
        __html: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Article",
          "headline": article.title,
          "description": article.excerpt,
          "image": article.coverImage,
          "datePublished": article.date,
          "dateModified": article.date,
          "author": {
            "@type": "Organization",
            "name": "美女性感魅力养成指南"
          },
          "publisher": {
            "@type": "Organization",
            "name": "美女性感魅力养成指南",
            "url": "https://yourdomain.com/"
          },
          "url": `https://yourdomain.com/article/${article.slug}`,
          "inLanguage": "zh-CN",
          "wordCount": article.content.length,
          "articleSection": catName,
          "keywords": article.tags.join(',')
        })
      }} />

      <script type="application/ld+json" dangerouslySetInnerHTML={{
        __html: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          "itemListElement": [
            {
              "@type": "ListItem",
              "position": 1,
              "name": "首页",
              "item": "https://yourdomain.com/"
            },
            {
              "@type": "ListItem",
              "position": 2,
              "name": catName,
              "item": `https://yourdomain.com/category/${article.category}`
            },
            {
              "@type": "ListItem",
              "position": 3,
              "name": article.title,
              "item": `https://yourdomain.com/article/${article.slug}`
            }
          ]
        })
      }} />
    </>
  );
}